import { useCallback, useState } from 'react';

import { toast } from 'react-toastify';
import { useShallow } from 'zustand/react/shallow';

import { useRoomContext } from '@src/contexts';
import SocketService from '@src/services/SocketService';
import { usePollsStore } from '@src/stores';

const useViewPolls = () => {
  const [isMenuPopperOpened, setIsMenuPopperOpened] = useState<Record<string, boolean>>({});

  const { currentPeer, canUserCreatePoll, isPollDeleteButtonEnabled } = useRoomContext();

  const { polls, singleVotesCounter, updatePoll, updatePollVote, setSingleVotesCounter } = usePollsStore(
    useShallow((state) => ({
      polls: state.polls,
      singleVotesCounter: state.singleVotesCounter,
      updatePoll: state.updatePoll,
      updatePollVote: state.updatePollVote,
      setSingleVotesCounter: state.setSingleVotesCounter,
    }))
  );

  const handleSingleVote = useCallback(
    (pollIndex: number, pollId: string, vote: string) => {
      const option = [vote];
      updatePollVote(pollId, option);
      SocketService.emit('vote', {
        pollIndex,
        pollId,
        option,
      });

      const newMap = new Map(singleVotesCounter);
      newMap.set(pollId, (newMap.get(pollId) || 0) + 1);
      setSingleVotesCounter(newMap);
    },
    [setSingleVotesCounter, singleVotesCounter, updatePollVote]
  );

  const handleMultipleVotes = useCallback(
    (pollIndex: number, pollId: string, vote: string) => {
      const currentPoll = polls?.[pollIndex];
      if (!currentPoll) return;

      let option: string[] = [];
      // webinar voting
      if (currentPoll.votingResults?.length) {
        const isOptionVoted = currentPoll?.myVote?.includes(vote);
        const currentOptionVotes = currentPoll.votingResults.find(({ option: opt }) => opt === vote);

        if (currentOptionVotes && isOptionVoted) {
          option = currentPoll.myVote?.filter((opt) => opt !== vote) || [];
        } else {
          option = [...(currentPoll.myVote || []), vote];
        }
      } else {
        // meeting voting
        const currentPeerVotes = currentPoll.voters?.[currentPeer?.peer_info.peer_id || ''];
        const isOptionVoted = currentPeerVotes?.voteSelection?.includes(vote);
        if (currentPeerVotes && isOptionVoted) {
          option = currentPeerVotes.voteSelection?.filter((opt) => opt !== vote) || [];
        } else {
          option = [...(currentPeerVotes?.voteSelection || []), vote];
        }
      }
      updatePollVote(pollId, option);
      SocketService.emit('vote', {
        pollId,
        pollIndex,
        option,
      });
    },
    [currentPeer?.peer_info.peer_id, polls, updatePollVote]
  );

  const getOptionVotersData = useCallback(
    (pollIndex: number, optionIndex: number) => {
      const { options, voters = {}, voteType, votingResults, myVote } = polls[pollIndex];
      const currentOption = options[optionIndex];
      let optionVoters: string[] = [];
      let count = 0;
      let percentage = 0;
      let isUserVoted = false;
      if (Object.values(voters).length) {
        optionVoters = Object.values(voters).reduce((acc: string[], { name, voteSelection }) => {
          if (voteSelection.includes(currentOption)) {
            acc.push(name);
          }
          return acc;
        }, []);
        let dividedBy = Object.values(voters).length;
        if (voteType === 'MULTIPLE') {
          dividedBy = Object.values(voters).reduce((acc: number, { voteSelection }) => {
            acc += voteSelection.length;
            return acc;
          }, 0);
        }
        count = optionVoters.length;
        percentage = Math.round((optionVoters.length * 100) / dividedBy);
        isUserVoted = !!optionVoters.find((voter) => voter === currentPeer?.peer_info.peer_name);
      } else if (votingResults) {
        const total = votingResults.reduce((acc, { count = 0 }) => {
          return acc + count;
        }, 0);
        count = votingResults[optionIndex]?.count ?? 0;
        percentage = total > 0 ? Math.round((count * 100) / total) : 0;
        isUserVoted = !!myVote?.includes(currentOption);
      }
      return {
        count,
        optionVoters,
        percentage,
        isUserVoted,
        hasVotes: !!optionVoters.length || !!votingResults?.length,
      };
    },
    [currentPeer?.peer_info.peer_name, polls]
  );

  const handleDeletePoll = useCallback(
    (pollId: string, index: number) => {
      SocketService.emit('deletePoll', {
        index,
        pollId,
        peer_name: currentPeer?.peer_info.peer_name,
        peer_uuid: currentPeer?.peer_info.peer_uuid,
      });
      setIsMenuPopperOpened((prev) => ({ ...prev, [pollId]: false }));
      toast.success('Poll deleted successfully!', {
        position: 'top-left',
        className: 'toast toast-black',
      });
    },
    [currentPeer?.peer_info.peer_name, currentPeer?.peer_info.peer_uuid]
  );

  const handleOnMenuClick = useCallback((id: string, isOpen: boolean) => {
    setIsMenuPopperOpened((prev) => ({ ...prev, [id]: isOpen }));
  }, []);

  const handlePollTimeLimit = useCallback(
    (id: string) => {
      updatePoll(id, { isVoteClosed: true });
    },
    [updatePoll]
  );

  return {
    polls,
    canUserCreatePoll,
    singleVotesCounter,
    isMenuPopperOpened,
    isPollDeleteButtonEnabled,

    handleSingleVote,
    handleDeletePoll,
    handleOnMenuClick,
    handleMultipleVotes,
    handlePollTimeLimit,
    getOptionVotersData,
  };
};

export default useViewPolls;
